import React from 'react';

import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';

import { MEDIA, TOKENS } from './tokens';

export const HomeGlobalStyles = () => (
  <Global
    styles={css`
      @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700&display=swap');

      .home-root {
        font-family: ${TOKENS.font.sans};
        -webkit-font-smoothing: antialiased;
      }

      @font-face {
        font-family: 'Avenir Next';
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src:
          local('Avenir Next Regular'),
          local('AvenirNext-Regular'),
          url('/fonts/avenirnext-regular.woff2') format('woff2');
      }

      @font-face {
        font-family: 'Avenir Next';
        font-style: normal;
        font-weight: 500;
        font-display: swap;
        src:
          local('Avenir Next Medium'),
          local('AvenirNext-Medium'),
          url('/fonts/avenirnext-medium.woff2') format('woff2');
      }

      @media (prefers-reduced-motion: reduce) {
        .home-root *,
        .home-root *::before,
        .home-root *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      }

      @keyframes home-mobile-nav-overlay-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes home-mobile-nav-content-in {
        from {
          opacity: 0;
          transform: translateY(-8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}
  />
);

export const HomeMobileOnly = styled.span`
  display: inline;
  @media (min-width: 1024px) {
    display: none;
  }
`;

export const HomeDesktopOnly = styled.span`
  display: none;
  @media (min-width: 1024px) {
    display: inline;
  }
`;

export const PageShell = styled.div`
  display: flex;
  min-height: 100svh;
  flex-direction: column;
  background: ${TOKENS.color.background};
  color: ${TOKENS.color.foreground};
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodyMdSize};
  line-height: ${TOKENS.type.bodyMdLineHeight};
  font-weight: ${TOKENS.type.bodyMdWeight};
`;

export const SectionContainer = styled.div`
  max-width: ${TOKENS.sectionMaxWidth};
  margin: 0 auto;
  width: 100%;
`;

const sectionPaddingX = css`
  padding-left: ${TOKENS.space[24]};
  padding-right: ${TOKENS.space[24]};

  ${MEDIA.md} {
    padding-left: ${TOKENS.space[40]};
    padding-right: ${TOKENS.space[40]};
  }

  ${MEDIA.lg} {
    padding-left: ${TOKENS.space[48]};
    padding-right: ${TOKENS.space[48]};
  }
`;

type SectionVariant =
  | 'storage'
  | 'security'
  | 'comparison'
  | 'testimonials'
  | 'brands'
  | 'faq';

const sectionPaddingY: Record<SectionVariant, ReturnType<typeof css>> = {
  storage: css`
    padding-top: ${TOKENS.space[48]};
    padding-bottom: ${TOKENS.space[80]};

    ${MEDIA.lg} {
      padding-top: ${TOKENS.space[128]};
      padding-bottom: ${TOKENS.space[128]};
    }
  `,
  security: css`
    padding-top: ${TOKENS.space[48]};
    padding-bottom: ${TOKENS.space[48]};

    ${MEDIA.lg} {
      padding-top: ${TOKENS.space[80]};
      padding-bottom: ${TOKENS.space[128]};
    }
  `,
  comparison: css`
    padding-top: ${TOKENS.space[40]};
    padding-bottom: ${TOKENS.space[80]};

    ${MEDIA.lg} {
      padding-top: ${TOKENS.space[80]};
      padding-bottom: ${TOKENS.space[128]};
    }
  `,
  testimonials: css`
    padding-top: ${TOKENS.space[40]};
    padding-bottom: ${TOKENS.space[80]};

    ${MEDIA.lg} {
      padding-top: ${TOKENS.space[64]};
      padding-bottom: ${TOKENS.space[64]};
    }
  `,
  brands: css`
    padding-top: ${TOKENS.space[48]};
    padding-bottom: ${TOKENS.space[80]};

    ${MEDIA.lg} {
      padding-top: ${TOKENS.space[128]};
      padding-bottom: ${TOKENS.space[128]};
    }
  `,
  faq: css`
    padding-top: ${TOKENS.space[40]};
    padding-bottom: ${TOKENS.space[80]};

    ${MEDIA.lg} {
      padding-top: ${TOKENS.space[80]};
      padding-bottom: ${TOKENS.space[128]};
    }
  `,
};

export const Section = styled.section<{
  $variant: SectionVariant;
  $muted?: boolean;
  $dark?: boolean;
}>`
  scroll-margin-top: ${TOKENS.headerOffset};
  ${sectionPaddingX}
  ${({ $variant }) => sectionPaddingY[$variant]}
  background: ${({ $muted, $dark }) => {
    if ($dark) return TOKENS.color.sectionBgDark;
    if ($muted) return TOKENS.color.backgroundMuted;
    return TOKENS.color.background;
  }};
  color: ${({ $dark }) =>
    $dark ? TOKENS.color.inverse : TOKENS.color.foreground};
`;

export const HeadingH1 = styled.h1`
  margin: 0;
  font-family: ${TOKENS.font.display};
  font-weight: ${TOKENS.type.headingH1Weight};
  font-size: ${TOKENS.type.headingH1Size};
  line-height: ${TOKENS.type.headingH1LineHeight};
  letter-spacing: 0;
  color: ${TOKENS.color.inverse};

  ${MEDIA.xl} {
    font-size: 72px;
    line-height: 88px;
  }

  ${MEDIA.xxl} {
    font-size: 80px;
    line-height: 96px;
  }
`;

export const HeadingH2 = styled.h2<{ $onDark?: boolean; $inverse?: boolean }>`
  margin: 0;
  font-family: ${TOKENS.font.display};
  font-weight: ${TOKENS.type.headingH2Weight};
  font-size: ${TOKENS.type.headingH2Size};
  line-height: ${TOKENS.type.headingH2LineHeight};
  letter-spacing: 0;
  color: ${({ $onDark, $inverse }) => {
    if ($inverse) return TOKENS.color.inverse;
    if ($onDark) return TOKENS.color.inverse;
    return TOKENS.color.foreground;
  }};
`;

export const HeadingH3 = styled.h3<{
  $onDark?: boolean;
  $inverse?: boolean;
  $muted?: boolean;
}>`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-weight: ${TOKENS.type.headingH3Weight};
  font-size: ${TOKENS.type.headingH3Size};
  line-height: ${TOKENS.type.headingH3LineHeight};
  letter-spacing: 0;
  color: ${({ $onDark, $inverse, $muted }) => {
    if ($inverse) return TOKENS.color.inverse;
    if ($onDark && $muted) return TOKENS.color.textSecondaryOnDark;
    if ($onDark) return TOKENS.color.inverse;
    return TOKENS.color.foreground;
  }};
`;

export const BodyMd = styled.p<{
  $onDark?: boolean;
  $muted?: boolean;
  $secondary?: boolean;
  $inverse?: boolean;
}>`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodyMdSize};
  line-height: ${TOKENS.type.bodyMdLineHeight};
  font-weight: ${TOKENS.type.bodyMdWeight};
  color: ${({ $onDark, $muted, $secondary, $inverse }) => {
    if ($inverse) return TOKENS.color.inverse;
    if ($onDark && $muted) return TOKENS.color.textSecondaryOnDark;
    if ($onDark) return TOKENS.color.inverse;
    if ($secondary) return TOKENS.color.foregroundSecondary;
    if ($muted) return TOKENS.color.foregroundMuted;
    return TOKENS.color.foreground;
  }};
`;

export const BodySm = styled.span<{
  $onDark?: boolean;
  $muted?: boolean;
  $secondary?: boolean;
}>`
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodySmSize};
  line-height: ${TOKENS.type.bodySmLineHeight};
  font-weight: ${TOKENS.type.bodySmWeight};
  color: ${({ $onDark, $muted, $secondary }) => {
    if ($onDark && $muted) return TOKENS.color.textSecondaryOnDark;
    if ($onDark) return TOKENS.color.inverse;
    if ($secondary) return TOKENS.color.foregroundSecondary;
    if ($muted) return TOKENS.color.foregroundMuted;
    return TOKENS.color.foreground;
  }};
`;

export const LabelSm = styled.span<{ $onDark?: boolean; $primary?: boolean }>`
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.labelSmSize};
  line-height: ${TOKENS.type.labelSmLineHeight};
  font-weight: ${TOKENS.type.labelSmWeight};
  text-transform: ${({ $primary }) => ($primary ? 'uppercase' : 'none')};
  color: ${({ $onDark, $primary }) => {
    if ($primary) return TOKENS.color.primary;
    if ($onDark) return TOKENS.color.onDark;
    return TOKENS.color.foreground;
  }};
`;

export const FeatureTitle = styled.span<{ $onDark?: boolean }>`
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.headingH3Size};
  line-height: ${TOKENS.type.headingH3LineHeight};
  font-weight: ${TOKENS.type.headingH3Weight};
  color: ${({ $onDark }) =>
    $onDark ? TOKENS.color.inverse : TOKENS.color.foreground};
`;

export const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${TOKENS.radius.sm};
  padding: ${TOKENS.space[12]} ${TOKENS.space[24]};
  background: ${TOKENS.color.primary};
  color: ${TOKENS.color.primaryForeground};
  border: 1px solid ${TOKENS.color.primary};
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodyMdSize};
  font-weight: ${TOKENS.type.bodyMdWeight};
  line-height: ${TOKENS.type.bodyMdLineHeight};
  text-decoration: none;
  white-space: nowrap;
  transition: filter 0.15s ease;

  &:hover {
    background: ${TOKENS.color.primaryHover};
    border-color: ${TOKENS.color.primaryHover};
    color: ${TOKENS.color.primaryForeground};
  }

  &:active {
    filter: brightness(0.9);
  }

  &:focus-visible {
    outline: 2px solid ${TOKENS.color.ring};
    outline-offset: 2px;
  }
`;

export const GhostButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${TOKENS.radius.sm};
  padding: ${TOKENS.space[12]} ${TOKENS.space[24]};
  background: transparent;
  color: ${TOKENS.color.onDark};
  border: 1px solid transparent;
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodyMdSize};
  font-weight: ${TOKENS.type.bodyMdWeight};
  line-height: ${TOKENS.type.bodyMdLineHeight};
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: ${TOKENS.color.neutral0_10};
    color: ${TOKENS.color.inverse};
  }

  &:active {
    background: ${TOKENS.color.neutral0_12};
  }

  &:focus-visible {
    outline: 2px solid ${TOKENS.color.ring};
    outline-offset: 2px;
  }
`;

/** Secondary button on section-bg-dark — Stone Blue fill, brand border, inverse label. */
export const secondaryOnDarkButtonStyles = css`
  background: ${TOKENS.color.foregroundSecondary};
  color: ${TOKENS.color.inverse};
  font-weight: 600;
  border: 1px solid ${TOKENS.color.primary};

  &:hover {
    background: ${TOKENS.color.primaryHover_24};
    color: ${TOKENS.color.inverse};
    border-color: ${TOKENS.color.primary};
  }

  &:active {
    background: ${TOKENS.color.foregroundSecondary};
    color: ${TOKENS.color.inverse};
    border-color: ${TOKENS.color.primary};
    filter: brightness(0.92);
  }
`;
